import axios, { AxiosError } from "axios";
import { AirQueryParams } from "./query.types";
import { AirResponseDTO } from "./response.types";
import { authInterceptor, sessionRequestConfig } from "shared/api/backofficeClient";
import { injectSearchCustomHeader } from "shared/api/utils/api.utils";
import { mutateAirMetadataInterceptor } from "./utils";

/**
 * Air search goes through the back office, like ocean search: the back office
 * holds the gateway key and counts guest searches by IP, with a counter of its
 * own so a guest's free air search does not spend the ocean one.
 */
const AIR_SEARCH_API_URL = "/api/tracking/search/air";

// Signed-in visitors are recognised by the cross-subdomain session cookie,
// which is also what lifts the guest limit.
const requestInstance = axios.create({ ...sessionRequestConfig });
authInterceptor(requestInstance);
injectSearchCustomHeader(requestInstance);

/**
 * A guest who has spent their free search gets a 429. The panel shows the
 * sign-in modal on requestLimitReachedWebApp, but only for a resolved
 * response - so this turns it back into one.
 */
requestInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ requestLimitReachedWebApp?: boolean }>) => {
    if (error.response?.status === 429 && error.response.data?.requestLimitReachedWebApp) {
      return Promise.resolve({
        ...error.response,
        data: { ...error.response.data, data: {} },
      });
    }

    return Promise.reject(error);
  }
);

mutateAirMetadataInterceptor(requestInstance);

class AirSearchService {
  public search(params: AirQueryParams, signal?: AbortSignal): Promise<AirResponseDTO> {
    const finalParams: AirQueryParams = {
      route_path: true,
      ...params,
    };

    return requestInstance.get(AIR_SEARCH_API_URL, { params: finalParams, ...(signal ? { signal } : {}) });
  }
}

const airSearchService = new AirSearchService();

/** @deprecated Use airSearchService */
const airTrackingService = airSearchService;

export { airSearchService, airTrackingService };
